// src/components/layout/AppShell.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import type { PlanKey } from "../../config/plans";
import { useI18n } from "../../i18n/i18n";
import { apiFetch, isApiError } from "../../lib/api";

type Mode = "creator" | "brand";

type MeResponse = {
  success: boolean;
  plan_id: PlanKey;
  credits_balance: number;
  allowedResolutions?: string[];
  error?: string;
};

export type ModeShellContext = {
  searchQuery: string;
  mePlan: PlanKey;
  credits: number | null;
  mode: Mode;
  isPreview: boolean;
};

const MODE_KEY = "appcreativa_mode";
const SIDEBAR_KEY = "ovylon_sidebar_open";

function prettyPlanName(plan: PlanKey): string {
  if (plan === "free") return "Free";
  if (plan === "basic") return "Basic";
  if (plan === "pro") return "Pro";
  return "Premium";
}

function isPlanKey(v: unknown): v is PlanKey {
  return v === "free" || v === "basic" || v === "pro" || v === "premium";
}

type NavItem = {
  to: string;
  labelKey: string;
  icon: React.ReactNode;
  modes?: Mode[];
  creatorTab?: "inicio" | "ia" | "proyectos" | "config";
};

const BRAND_NAV: NavItem[] = [
  { to: "/brand", labelKey: "nav.home", icon: "🏠", modes: ["brand"] },
  { to: "/brand/create", labelKey: "nav.generator", icon: "✨", modes: ["brand"] },
  { to: "/brand/projects", labelKey: "nav.projects", icon: "📁", modes: ["brand"] },
  { to: "/brand/kit", labelKey: "nav.brandKit", icon: "🎛️", modes: ["brand"] },
  { to: "/brand/settings", labelKey: "nav.settings", icon: "⚙️", modes: ["brand"] },
];

const CREATOR_NAV: NavItem[] = [
  { to: "/dashboard", labelKey: "nav.home", icon: "🏠", modes: ["creator"], creatorTab: "inicio" },
  { to: "/dashboard", labelKey: "nav.generator", icon: "✨", modes: ["creator"], creatorTab: "ia" },
  { to: "/dashboard", labelKey: "nav.projects", icon: "📁", modes: ["creator"], creatorTab: "proyectos" },
  { to: "/dashboard", labelKey: "nav.settings", icon: "⚙️", modes: ["creator"], creatorTab: "config" },
];

export default function AppShell({ mode, minimal = false }: { mode: Mode; minimal?: boolean }) {
  const { user, loading, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t } = useI18n();

  const isPreview = !user;

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_KEY);
      if (raw === "0") return false;
      if (raw === "1") return true;
      return true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, sidebarOpen ? "1" : "0");
    } catch {
      // ignore
    }
  }, [sidebarOpen]);

  const [searchQuery, setSearchQuery] = useState("");
  const [mePlan, setMePlan] = useState<PlanKey>("free");
  const [credits, setCredits] = useState<number | null>(null);

  // ─────────────────────────────────────────────
  // ✅ Navegación segura (anti-loops)
  // ─────────────────────────────────────────────
  const lastNavRef = useRef<string | null>(null);
  const safeReplace = useCallback(
    (to: string) => {
      const current = location.pathname + (location.search || "");
      if (current === to) return;
      if (lastNavRef.current === to) return;
      lastNavRef.current = to;
      navigate(to, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  useEffect(() => {
    lastNavRef.current = null;
  }, [location.pathname]);

  // ✅ Persistimos el modo actual SOLO si hay usuario (evita ensuciar preview)
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode, loading, user]);

  const navItems = useMemo(() => {
    const base = mode === "brand" ? BRAND_NAV : CREATOR_NAV;
    return base.filter((i) => !i.modes || i.modes.includes(mode));
  }, [mode]);

  const previewGeneratorTarget = useMemo(() => {
    if (mode === "brand") return "/brand/create";
    return "/dashboard?tab=ia";
  }, [mode]);

  const goLoginWithNext = useCallback(
    (nextPath: string) => {
      const next = encodeURIComponent(nextPath);
      safeReplace(`/login?next=${next}`);
    },
    [safeReplace]
  );

  // ─────────────────────────────────────────────
  // ✅ /api/me (backend) — anti-spam + anti-429
  // ─────────────────────────────────────────────
  const meInFlightRef = useRef(false);
  const meLastAtRef = useRef(0);
  const meRetryTimerRef = useRef<number | null>(null);

  const clearMeRetryTimer = useCallback(() => {
    if (meRetryTimerRef.current) {
      window.clearTimeout(meRetryTimerRef.current);
      meRetryTimerRef.current = null;
    }
  }, []);

  const setMeFallbackFromAuth = useCallback(() => {
    const p = (user as any)?.plan;
    setMePlan(isPlanKey(p) ? p : "free");
    setCredits(null);
  }, [user]);

  const loadMe = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!user) {
        // ✅ si no hay user, cancelamos retries pendientes y reseteamos
        clearMeRetryTimer();
        setMePlan("free");
        setCredits(null);
        return;
      }

      const now = Date.now();
      const minGap = opts?.force ? 0 : 2500;
      if (!opts?.force && now - meLastAtRef.current < minGap) return;
      if (meInFlightRef.current) return;

      meInFlightRef.current = true;
      meLastAtRef.current = now;

      try {
        const token = await getAccessToken();
        if (!token) {
          setMeFallbackFromAuth();
          return;
        }

        const data = await apiFetch<MeResponse>("/api/me", {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!data?.success) {
          setMeFallbackFromAuth();
          return;
        }

        setMePlan(isPlanKey(data.plan_id) ? data.plan_id : "free");
        setCredits(typeof data.credits_balance === "number" ? data.credits_balance : 0);
      } catch (e) {
        if (isApiError(e) && e.status === 429) {
          clearMeRetryTimer();
          meRetryTimerRef.current = window.setTimeout(() => loadMe({ force: true }), 3500);
          return;
        }

        if (isApiError(e) && (e.status === 401 || e.status === 403)) {
          setMeFallbackFromAuth();
          return;
        }

        setMeFallbackFromAuth();
      } finally {
        meInFlightRef.current = false;
      }
    },
    [user, getAccessToken, setMeFallbackFromAuth, clearMeRetryTimer]
  );

  useEffect(() => {
    if (loading) return;

    if (!user) {
      // ✅ IMPORTANTE: cancelamos retry timer si el user desaparece
      clearMeRetryTimer();
      setMePlan("free");
      setCredits(null);
      return;
    }

    clearMeRetryTimer();
    loadMe({ force: true });

    return () => {
      clearMeRetryTimer();
    };
  }, [loading, user, loadMe, clearMeRetryTimer]);

  useEffect(() => {
    if (loading) return;
    if (!user) return;

    const params = new URLSearchParams(location.search);
    if (params.get("stripe") !== "success") return;

    loadMe({ force: true });
    safeReplace(location.pathname);
  }, [location.search, location.pathname, loadMe, safeReplace, loading, user]);

  const outletContext = useMemo(
    (): ModeShellContext => ({ searchQuery, mePlan, credits, mode, isPreview }),
    [searchQuery, mePlan, credits, mode, isPreview]
  );

  // ✅ Para Creator: active por query param tab=
  const activeCreatorTab = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const v = p.get("tab") || "inicio";
    if (v === "ia" || v === "proyectos" || v === "config" || v === "inicio") return v;
    return "inicio";
  }, [location.search]);

  const mainPad = mode === "brand" ? "p-4 md:p-6" : "p-6 md:p-8";

  if (minimal) {
    return (
      <div className="min-h-screen bg-[#05050a] text-white">
        <main className={`min-h-screen ${mainPad} bg-gradient-to-br from-indigo-950 via-black to-slate-950`}>
          <Outlet context={outletContext} />
        </main>
      </div>
    );
  }

  const topbarBtnBase =
    "h-10 inline-flex items-center justify-center rounded-xl px-4 text-sm font-medium transition " +
    "outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-0";

  const topbarBtnGhost =
    topbarBtnBase + " border border-white/12 bg-white/5 text-white/90 hover:bg-white/10 hover:border-white/18";

  const topbarBtnPrimary =
    topbarBtnBase + " bg-indigo-500 text-white shadow-sm shadow-indigo-500/20 hover:bg-indigo-400";

  const langPill = "h-10 inline-flex items-center rounded-xl border border-white/12 bg-white/5 p-1";
  const langBtn =
    "h-8 px-3 rounded-lg text-xs font-semibold transition outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40";

  // ✅ OJO: aquí dejamos /register (si tu ruta es /signup, cámbialo en 2 lugares)
  const REGISTER_PATH = "/register";

  return (
    <div className="flex min-h-screen bg-[#05050a] text-white">
      {/* SIDEBAR */}
      <aside
        className={`
          ${sidebarOpen ? "w-72" : "w-20"}
          bg-[#05050b] border-r border-white/10 transition-all duration-300
          flex flex-col py-6 relative
          h-screen sticky top-0
        `}
      >
        {/* Logo */}
        <div className="px-6 mb-10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-400 flex items-center justify-center text-xl font-bold shadow-lg shadow-indigo-500/30">
            O
          </div>
          {sidebarOpen && (
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Ovylon</h1>
              <p className="text-[11px] text-gray-400 uppercase tracking-[0.2em]">
                {mode === "brand" ? t("shell.brandStudio") : t("shell.creatorStudio")}
              </p>
            </div>
          )}
        </div>

        {/* NAV */}
        <nav className="flex flex-col gap-1 px-3 flex-1 overflow-y-auto">
          {navItems.map((item) => {
            const label = t(item.labelKey);

            const isGeneratorItem =
              item.to === "/brand/create" || item.labelKey === "nav.generator" || item.creatorTab === "ia";

            if (isPreview) {
              return (
                <button
                  key={item.labelKey + item.to}
                  type="button"
                  onClick={() => {
                    if (isGeneratorItem) {
                      safeReplace(previewGeneratorTarget);
                      return;
                    }

                    const nextTarget =
                      mode === "creator" ? `/dashboard?tab=${item.creatorTab || "inicio"}` : item.to;

                    goLoginWithNext(nextTarget);
                  }}
                  className={`
                    flex items-center gap-3 p-3 rounded-xl transition text-sm text-left
                    hover:bg-white/5
                    ${isGeneratorItem ? "opacity-100" : "opacity-70"}
                  `}
                  title={isGeneratorItem ? label : t("shell.loginRequired")}
                >
                  <span className="text-lg">{item.icon}</span>
                  {sidebarOpen && <span>{label}</span>}
                </button>
              );
            }

            if (mode === "creator") {
              const tab = item.creatorTab || "inicio";
              const isActive = activeCreatorTab === tab;

              return (
                <button
                  key={item.labelKey + tab}
                  type="button"
                  onClick={() => safeReplace(`/dashboard?tab=${tab}`)}
                  className={`
                    flex items-center gap-3 p-3 rounded-xl transition text-sm text-left
                    ${
                      isActive
                        ? "bg-gradient-to-r from-indigo-500/40 to-purple-500/40 border border-indigo-400/40"
                        : "hover:bg-white/5"
                    }
                  `}
                >
                  <span className="text-lg">{item.icon}</span>
                  {sidebarOpen && <span>{label}</span>}
                </button>
              );
            }

            const isHomeLink = item.to === "/brand";
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={isHomeLink}
                className={({ isActive }) =>
                  `flex items-center gap-3 p-3 rounded-xl transition text-sm
                   ${
                     isActive
                       ? "bg-gradient-to-r from-indigo-500/40 to-purple-500/40 border border-indigo-400/40"
                       : "hover:bg-white/5"
                   }`
                }
              >
                <span className="text-lg">{item.icon}</span>
                {sidebarOpen && <span>{label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Toggle sidebar */}
        <button
          onClick={() => setSidebarOpen((v) => !v)}
          className="mx-auto mt-4 mb-4 p-2 hover:bg-white/10 rounded-xl text-xs border border-white/10"
          title={sidebarOpen ? t("shell.collapseMenu") : t("shell.expandMenu")}
        >
          {sidebarOpen ? "⬅" : "➡"}
        </button>
      </aside>

      {/* CONTENT */}
      <div className="flex-1 flex flex-col">
        {/* TOPBAR */}
        <header className="w-full flex justify-between items-center px-8 py-4 border-b border-white/10 bg-[#070711]/90 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                id="search"
                name="search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={mode === "brand" ? t("shell.searchBrand") : t("shell.searchCreator")}
                className="w-72 px-10 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-200 text-sm focus:ring-2 focus:ring-indigo-500/40 outline-none"
              />
              <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
            </div>
          </div>

          <div className="flex items-center gap-6">
            {/* USER INFO */}
            <div className="text-right text-xs leading-tight">
              <p className="text-gray-200 font-medium">{user?.email || t("shell.exploreNoSession")}</p>
              <p className="text-gray-400 text-[11px]">
                {isPreview
                  ? t("shell.previewHint")
                  : `${prettyPlanName(mePlan)} · ${
                      credits === null ? t("shell.creditsLoading") : `${credits} ${t("shell.credits")}`
                    }`}
              </p>
            </div>

            {/* LANGUAGE */}
            <div className={langPill} aria-label={t("common.language")}>
              <button
                type="button"
                onClick={() => setLang("es")}
                className={`${langBtn} ${
                  lang === "es" ? "bg-white/12 text-white" : "text-white/70 hover:text-white hover:bg-white/8"
                }`}
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={`${langBtn} ${
                  lang === "en" ? "bg-white/12 text-white" : "text-white/70 hover:text-white hover:bg-white/8"
                }`}
              >
                EN
              </button>
            </div>

            {/* LOGIN / SIGNUP (preview only) */}
            {isPreview && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const next = encodeURIComponent(location.pathname + (location.search || ""));
                    safeReplace(`/login?next=${next}`);
                  }}
                  className={topbarBtnGhost}
                >
                  {t("shell.login")}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const next = encodeURIComponent(location.pathname + (location.search || ""));
                    safeReplace(`${REGISTER_PATH}?next=${next}`);
                  }}
                  className={topbarBtnPrimary}
                >
                  {t("shell.signup")}
                </button>
              </div>
            )}
          </div>
        </header>

        {/* MAIN */}
        <main className={`flex-1 ${mainPad} bg-gradient-to-br from-indigo-950 via-black to-slate-950`}>
          <Outlet context={outletContext} />
        </main>
      </div>
    </div>
  );
}






